import React from 'react';
import { motion } from 'framer-motion';
import { getConfig } from '../../common/env-config';

const ManageCampaigns = () => {
    const { ecWebsiteUrl } = getConfig();
    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <div className="font-sans text-gray-900 bg-white">

            {/* HERO */}
            <section className="bg-[hsl(264,50%,96%)] relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/30 to-transparent pointer-events-none"></div>
                <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-14 items-center relative z-10">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <span className="inline-block mb-4 px-4 py-1 rounded-full bg-black text-white text-xs tracking-wide">Hyperlocal Campaign Management</span>
                        <h1 className="text-4xl md:text-6xl font-extrabold leading-tight pb-2 bg-gradient-to-r from-gray-900 via-primary-700 to-[#F482C0] bg-clip-text text-transparent">
                            Manage & Measure Hyperlocal Creator Campaigns — <span className="underline decoration-gray-400 text-gray-900">End to End</span>
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 max-w-xl">
                            Oye Creators is a campaign-first platform that helps brands plan, execute, and track hyperlocal creator campaigns with clear visibility on reach, engagement, enquiries, and real business outcomes.
                        </p>
                        <motion.div className="mt-10 flex gap-4" whileHover={{ scale: 1.02 }}>
                            <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-primary-500 text-white rounded-2xl font-bold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/30 inline-block">Create a Campaign</a>
                            <a href={ecWebsiteUrl + "signup"} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-white border border-gray-300 rounded-2xl font-bold text-gray-700 hover:bg-gray-50 transition-colors inline-block">View Campaign Results</a>
                        </motion.div>
                    </motion.div>

                    {/* CAMPAIGN CARD */}
                    <motion.div
                        className="bg-white rounded-3xl shadow-2xl p-8"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-sm text-gray-500 mb-3">Campaign setup example</div>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between bg-gray-50 rounded-xl px-4 py-3">
                                <span>Campaign Area</span><span className="font-medium">Baner • Aundh • Wakad</span>
                            </div>
                            <div className="flex justify-between bg-gray-50 rounded-xl px-4 py-3">
                                <span>Creator Type</span><span className="font-medium">Food & Lifestyle (5k–30k)</span>
                            </div>
                            <div className="flex justify-between bg-gray-50 rounded-xl px-4 py-3">
                                <span>Objective</span><span className="font-medium">Footfall & Orders</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* WHY CAMPAIGN MANAGEMENT */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl font-bold">Why campaign management matters</h2>
                        <p className="mt-4 text-gray-600">Posting content is easy. Managing outcomes is hard.</p>
                    </motion.div>
                    <motion.div
                        className="md:col-span-2 grid md:grid-cols-3 gap-6"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="border border-gray-100 rounded-2xl p-6 hover:border-black transition-colors bg-gray-50 group hover:shadow-lg">
                            <div className="mb-2 text-xl scale-125 origin-left opacity-80 group-hover:scale-100 transition-transform">❌</div>
                            <div className="font-medium">Disconnected creators & no control</div>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="border border-gray-100 rounded-2xl p-6 hover:border-black transition-colors bg-gray-50 group hover:shadow-lg">
                            <div className="mb-2 text-xl scale-125 origin-left opacity-80 group-hover:scale-100 transition-transform">❌</div>
                            <div className="font-medium">No area-wise performance clarity</div>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="border border-gray-100 rounded-2xl p-6 hover:border-black transition-colors bg-gray-50 group hover:shadow-lg">
                            <div className="mb-2 text-xl scale-125 origin-left opacity-80 group-hover:scale-100 transition-transform">❌</div>
                            <div className="font-medium">Vanity metrics, no business insights</div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* OYE FLOW */}
            <section className="bg-[#F9B4DA1A] py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div
                        className="text-center max-w-3xl mx-auto mb-20"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold">How Brands Run Campaigns on <span className="text-[#F482C0]">Oye Creators</span></h2>
                        <p className="text-gray-600 mt-4">
                            A structured workflow built for marketers who need predictability, control, and measurable ROI.
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid md:grid-cols-5 gap-6"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {[
                            { step: '01', title: 'Create Campaign', desc: 'Select objective: awareness, footfall, leads or launches.' },
                            { step: '02', title: 'Define Area', desc: 'Choose city → locality → society clusters.' },
                            { step: '03', title: 'Assign Creators', desc: 'Pick trusted local creators matched to the area.' },
                            { step: '04', title: 'Monitor Live', desc: 'Track content, engagement and creator compliance.' },
                            { step: '05', title: 'Measure Results', desc: 'View area-wise ROI, leads and conversions.' }
                        ].map((item, idx) => (
                            <motion.div variants={fadeInUp} key={idx} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl transition-all border border-white hover:border-pink-200 group">
                                <div className="text-xs font-bold text-gray-300 group-hover:text-primary-500 transition-colors tracking-widest mb-4">STEP {item.step}</div>
                                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* RESULTS DASHBOARD */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.h2
                        className="text-3xl font-bold text-center mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        Campaign Results That Businesses Care About
                    </motion.h2>

                    <motion.div
                        className="grid md:grid-cols-4 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {[
                            { title: 'Area-wise Reach', desc: 'Understand which localities actually responded.' },
                            { title: 'Cost per Enquiry', desc: 'Know how much each lead or visit really cost.' },
                            { title: 'Creator Performance', desc: 'Identify which creators drive real action.' },
                            { title: 'Campaign ROI', desc: 'Link spends to measurable business outcomes.' }
                        ].map((item, idx) => (
                            <motion.div variants={fadeInUp} key={idx} className="border border-gray-200 rounded-3xl p-8 hover:border-primary-500 transition-colors group hover:bg-gray-50">
                                <h3 className="font-bold text-lg group-hover:text-primary-600 transition-colors">{item.title}</h3>
                                <p className="text-sm text-gray-600 mt-3">{item.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* LOGICAL EXAMPLES */}
            <section className="bg-[#AC8EE11A] py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.h2
                        className="text-3xl font-bold mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        How <span className="text-[#F482C0]">Hyperlocal Campaigns</span> Benefit Businesses
                    </motion.h2>

                    <motion.div
                        className="grid md:grid-cols-3 gap-10"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all border border-transparent hover:border-primary-200">
                            <h3 className="font-bold text-xl mb-4">🏪 Retail Store Launch</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">A retail brand runs a 14-day campaign with 12 creators across 3 localities. Result: high recall, steady walk-ins, and lower cost per visit compared to paid ads.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all border border-transparent hover:border-primary-200">
                            <h3 className="font-bold text-xl mb-4">🏘 Real Estate Site Visits</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">Local creators explain connectivity, lifestyle and pricing. Leads generated are already area-aware, improving site visit quality.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all border border-transparent hover:border-primary-200">
                            <h3 className="font-bold text-xl mb-4">🍽 Restaurant Footfall</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">Creators from nearby societies share genuine visits. Result: immediate footfall during campaign period.</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-primary-500 text-white py-24">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl md:text-5xl font-extrabold pb-2">Run fewer campaigns. Get better results.</h2>
                        <p className="mt-4 text-white/90 max-w-2xl mx-auto text-xl">Oye Creators helps you manage hyperlocal campaigns with clarity, control and confidence.</p>
                        <motion.a
                            href={ecWebsiteUrl + "signup"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-10 px-10 py-5 bg-white text-primary-600 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-2xl inline-block"
                            whileHover={{ scale: 1.05 }}
                        >
                            Launch Your First Campaign
                        </motion.a>
                    </motion.div>
                </div>
            </section>

        </div>
    );
};

export default ManageCampaigns;
